'use es6';

import I18n from 'I18n';
import { OrderedSet } from 'immutable';
import invariant from 'react-utils/invariant';
import { useCallback, useMemo, useState } from 'react';
import { ConfirmModal } from 'foundations-components/transitional/components/ConfirmModal';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';

// NavigationConfirmation is a registry of callbacks which are executed
// when the user navigates to another page.
//
// If one or more of the registered callbacks return a warning message,
// they will be prompted with that message before leaving.
//
// For example...
//
//   ```
//   getNavigationConfirmation() {
//     if (!hasChanges) {
//       return;
//     }
//     return I18n.text('unsavedChangesWarning');
//   }
//
//
//   componentDidMount() {
//     register(this.handleGetNavigationConfirmation);
//   }
//   ```
//
// It's important to unregister your callback once it's no longer needed...
//
//   ```
//   componentWillUnmount() {
//     unregister(this.handleGetNavigationConfirmation);
//   }
//   ```
import { jsx as _jsx } from "react/jsx-runtime";
let registered = OrderedSet();
export function getRegistered() {
  return registered;
}
export function runChecks() {
  return registered.reduce((confirmations, checker) => {
    const message = checker();
    if (message == null) {
      return confirmations;
    }
    confirmations.push(message);
    return confirmations;
  }, []);
}

/*
 * This is meant to be used as a handler for window.onbeforeunload.
 *
 * @param  {BeforeUnloadEvent}
 * @return {?string}
 */
export function confirmUnload(evt) {
  invariant(evt != null && typeof evt === 'object', 'expected `evt` to be an object but got `%s`', evt);
  const confirmations = runChecks();
  if (confirmations.length === 0) {
    return undefined;
  }
  return evt.returnValue = confirmations.join('\n\n');
}

/**
 * @typedef {Function} HandleConfirmNavigation
 * @param {() => void} onConfirm - A function to be called when the user confirms the navigation.
 * @param {() => void} onReject - A function to be called when the user rejects the navigation.
 */

/**
 * @typedef {Object} UseConfirmNavigationReturn
 * @property {React.ReactNode | null} confirmNavigationModal - A modal to be displayed when the user navigates away from the current page.
 * @property {HandleConfirmNavigation} handleConfirmNavigation - A function to be called when the user confirms the navigation.
 */

/*
 * Used for confirming navigation other routes within the app.
 *
 * @return {UseConfirmNavigationReturn}
 */
export function useConfirmNavigation() {
  const [modal, setModal] = useState(null); // type: React.ReactNode | null
  const handleConfirmNavigation = useCallback(({
    onConfirm,
    onReject
  }) => {
    const confirmations = runChecks();
    if (confirmations.length === 0) {
      if (onConfirm) {
        onConfirm();
      }
    } else {
      setModal( /*#__PURE__*/_jsx(ConfirmModal, {
        description: confirmations.map((message, i) => /*#__PURE__*/_jsx(Paragraph, {
          children: message
        }, i)),
        message: I18n.text('customerDataUiUtilities.NavigationConfirmation.abandonChanges.title'),
        confirmLabel: I18n.text('customerDataUiUtilities.NavigationConfirmation.abandonChanges.leave'),
        rejectLabel: I18n.text('customerDataUiUtilities.NavigationConfirmation.abandonChanges.stay'),
        onConfirm: () => {
          if (onConfirm) onConfirm();
          setModal(null);
        },
        onReject: () => {
          if (onReject) onReject();
          setModal(null);
        }
      }));
    }
  }, []);
  const retVal = useMemo(() => {
    return {
      confirmNavigationModal: modal,
      handleConfirmNavigation
    };
  }, [modal, handleConfirmNavigation]);
  return retVal;
}
export function enforceFunction(checker) {
  invariant(typeof checker === 'function', 'expected `checker` to be a function but got `%s`', checker);
  return checker;
}

/*
 * Add a callback to be checked before navigation.
 * Throws if `checker` is not a function.
 *
 * `checker` should return either `undefined` (if no confirmation is needed)
 * or a translated string describing the changes that will be lost if the
 * user proceeds to navigate away from the certain page.
 *
 * @param  {() => ?string}
 * @return {void}
 */
export function register(checker) {
  registered = registered.add(enforceFunction(checker));
}

/*
 * Remove a callback that was previously registered.
 * Throws if `checker` is not a function.
 *
 * @param  {() => ?string}
 * @return {void}
 */
export function unregister(checker) {
  registered = registered.remove(enforceFunction(checker));
}
export function clearRegistrations() {
  registered = OrderedSet();
}